import { useCallback, useEffect, useRef, useState } from 'react';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Button, Stack, Typography } from '@mui/material';
import { AppMediaClient } from '@@api';
import { useApiClient, useToastService } from '@@common';

import { EmbeddedResourceModel } from '../Models/EmbeddedResourceModel';

function detectMimeType(base64: string): string {
	if (base64.startsWith('/9j/')) return 'image/jpeg';
	if (base64.startsWith('iVBOR')) return 'image/png';
	if (base64.startsWith('R0lG')) return 'image/gif';
	if (base64.startsWith('UklGR')) return 'image/webp';
	if (base64.startsWith('PHN2Z') || base64.startsWith('PD94bW')) return 'image/svg+xml';
	return 'image/png';
}

type AppResourceDesignerProps = {
	definitionId: string;
	changeSetId?: string;
	readOnly?: boolean;
	model: EmbeddedResourceModel;
	setModel: (model: EmbeddedResourceModel) => void;
};

export function AppResourceDesigner({ definitionId, changeSetId, readOnly = false, model, setModel }: AppResourceDesignerProps) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const upload = useRef<AbortController>();
	const client = useApiClient(AppMediaClient);
	const toast = useToastService();
	const [uploading, setUploading] = useState(false);
	const [preview, setPreview] = useState<{ fileId: string; url?: string }>();
	useEffect(() => {
		setUploading(false);
		return () => upload.current?.abort();
	}, [model.Id]);
	useEffect(() => {
		if (!model.FileId) return;
		const controller = new AbortController();
		client.get(definitionId, model.Id, model.FileId, changeSetId, controller.signal).then((file) => {
			if (!controller.signal.aborted) setPreview({ fileId: model.FileId!, url: file.url });
		}).catch((error) => { if (!controller.signal.aborted) toast.error(error instanceof Error ? error.message : 'Unable to load image'); });
		return () => controller.abort();
	}, [client, definitionId, changeSetId, model.Id, model.FileId, toast]);

	const handleUploadClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			e.target.value = '';
			if (!file || readOnly) return;
			if (!file.type.startsWith('image/') || file.size > 20 * 1024 * 1024) {
				toast.error('Choose an image no larger than 20 MB.');
				return;
			}
			upload.current?.abort();
			const controller = new AbortController();
			upload.current = controller;
			setUploading(true);
			try {
				const result = await client.upload(definitionId, changeSetId, undefined, { data: file, fileName: file.name }, controller.signal);
				if (!controller.signal.aborted) {
					setPreview({ fileId: result.id, url: result.url });
					setModel({ ...model, Name: model.Name || file.name, FileId: result.id, Content: '' });
				}
			} catch (error) {
				if (!controller.signal.aborted) toast.error(error instanceof Error ? error.message : 'Unable to upload image');
			} finally {
				if (!controller.signal.aborted) setUploading(false);
			}
		},
		[model, setModel, readOnly, client, definitionId, changeSetId, toast],
	);

	const imageSrc = model.FileId ? (preview?.fileId === model.FileId ? preview.url : undefined)
		: model.Content ? `data:${detectMimeType(model.Content)};base64,${model.Content}` : undefined;

	return (
		<Stack
			direction="column"
			alignItems="center"
			justifyContent="center"
			sx={(theme) => ({
				flexGrow: 1,
				gap: 2,
				p: 2,
				backgroundImage: `linear-gradient(45deg, ${theme.palette.background.paper} 25%, transparent 25%), linear-gradient(-45deg, ${theme.palette.background.paper} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${theme.palette.background.paper} 75%), linear-gradient(-45deg, transparent 75%, ${theme.palette.background.paper} 75%)`,
				backgroundSize: '16px 16px',
				backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
				backgroundColor: theme.palette.background.default,
			})}
		>
			{imageSrc ? (
				<Box
					component="img"
					src={imageSrc}
					alt={model.Name}
					sx={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
				/>
			) : (
				<Typography variant="body2" color="textSecondary">
					No image content
				</Typography>
			)}
			<input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
			<Button variant="contained" startIcon={<UploadFileIcon />} onClick={handleUploadClick} disabled={readOnly || uploading} loading={uploading}>
				Upload Image
			</Button>
			{!readOnly && <Typography variant="caption" color="text.secondary">Images are stored in Files. Save the App to keep this media reference.</Typography>}
		</Stack>
	);
}
